import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { DateTime as LuxonDateTime } from 'luxon';

import { BookJob } from 'src/providers/book-job/book-job';
import { CalendarDay } from 'src/providers/calendar/calendar-day';
import { Card } from 'src/providers/card/card';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Logger } from 'src/providers/logger';
import { Me } from 'src/providers/me/me';
import { PlanData } from 'src/providers/plans/plan-data';
import { Pros } from 'src/providers/pros/pros';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Schedule } from 'src/providers/schedule/schedule';

import { Loading } from 'src/shared/components/loading/loading';
import { Util } from 'src/shared/util/util';

import { TidySelectStringValueModel } from 'src/models/tidy-select-item.model';

import { TimeRanges } from 'src/providers/time-ranges/time-ranges';
import { ConfirmBookingPage } from '../booking/confirm-booking/confirm-booking';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { AddProPage } from '../my-pros/add-pro/add-pro';
import { ConfirmBookingPaymentPage } from '../confirm-booking-payment/confirm-booking-payment';
import { BookService } from 'src/shared/services/book.service';

@Component({
  standalone: false,
  templateUrl: 'request-job.html',
})

export class RequestJobPage implements OnInit, OnDestroy {

  address;
  allTimesOpts: TidySelectStringValueModel[];
  attachments: any;
  bookingExperiment: any;
  bookingId: string;
  bookingType: string;
  cameFromBookOrRequest: any;
  customBackPage: string;
  currentDateTime: any;
  customFlowName: string | null;
  customFlowSmallImage: string | null;
  customFlowDiscount: string | null;
  dateLabel;
  didChooseService = true;
  didSelectAllTimes: boolean;
  errorMessage: string;
  form: UntypedFormGroup;
  forms: any;
  frequencyItems: TidySelectStringValueModel[] = [
    { viewValue: 'One Time', value: 'once' },
    { viewValue: 'Weekly', value: 'every_week' },
    { viewValue: 'Every 2 Weeks', value: 'every_other_week' },
    { viewValue: 'Every 4 Weeks', value: 'every_four_weeks' },
  ];
  isRentalClient: boolean;
  isPrivateFlow: any;
  hasPrivatePros: boolean;
  hasNoPrivatePros: boolean;
  hasPros: boolean;
  hasNonTrialPaidSubscription: boolean;
  noLaterTimes: TidySelectStringValueModel[];
  selectedFrequency: string;
  noEarlierTimes: TidySelectStringValueModel[];
  priceExperiment: any;
  proId: any;
  priorityListSentence: string;
  allowTimesToday: any;
  hasActiveSubscription: boolean;
  hasImageUploadError: boolean;
  probabilityData: any;
  planId: any;
  showTimesError: boolean;
  service: any;
  services: any;
  serviceTypeId: any;
  showMoreServices: boolean;
  showSameDayTimeAlert: boolean;
  selectTimesTitle;
  selectedCheckboxes: any;
  submitted: boolean;
  tooFarStartDateError: boolean;
  understandExtraCost: boolean;
  waterfallSettings: any;
  dialogParams: any;
  isRightSideContent = true;
  providedStartDate: string;
  @ViewChild('noEarlierDate') noEarlierDate: any;
  @ViewChild('dynamicBookingForm') dynamicBookingForm: any;
  loaded: boolean;

  // Dynamic booking form related properties
  isLoadingDynamicForm = false;
  originalJobData: any;
  private preservedFormData: any = null;
  private formDataMonitoringInterval: any = null;
  private hasAttemptedToLoadOriginalData = false;
  private hasLoadedIncomingFormData = false;


  boundTurnOnTidyFindNewPros = this.turnOnTidyFindNewPros.bind(this);
  boundGoToAddPro = this.goToAddPro.bind(this);
  boundSubmit = this.submit.bind(this);

constructor(
    public bookJobService: BookJob,
    private calendarDay: CalendarDay,
    private card: Card,
    private client: Client,
    private fb: UntypedFormBuilder,
    private me: Me,
    private navCtrl: CustomNavController,
    private logger: Logger,
    private pros: Pros,
    private planData: PlanData,
    private storage: TidyStorage,
    private schedule: Schedule,
    private timeRanges: TimeRanges,
    private util: Util,
    public windowService: WindowService,
    private rightSidePanelService: RightSidePanelService,
    private bookService: BookService,
  ) {
    this.form = this.fb.group({
      service: ['', Validators.required],
      frequency: ['once'],
      noEarlierDate: ['', Validators.required],
      noEarlierTime: ['', Validators.required],
      noLaterDate: ['', Validators.required],
      noLaterTime: ['', Validators.required],
      jobNote: ['']
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.rightSidePanelService.setDialogPageTitle('Request Job');
    this.loadDataAndParams();
  }

  ngOnDestroy() {
    // Clean up monitoring interval
    this.stopFormDataMonitoring();
    // Synchronously persist current booking-form state back into
    // book-job-chooser-restore-state so BookJobPage picks up any edits the user
    // made here if they navigate back. localStorage is synchronous, which avoids
    // the race against BookJob's ngOnInit reading state too early.
    this.persistFormStateForBookJobRestore();
  }

  private persistFormStateForBookJobRestore(): void {
    try {
      let bookingFormAnswers: any = null;
      if (this.dynamicBookingForm?.isReady) {
        bookingFormAnswers = this.dynamicBookingForm.buildBookingFormData();
      } else if (this.preservedFormData) {
        bookingFormAnswers = this.preservedFormData;
      }
      if (!bookingFormAnswers || !bookingFormAnswers.input_answers?.length) {return;}
      const raw = localStorage.getItem('book-job-chooser-restore-state');
      if (!raw) {return;}
      let existing: any = null;
      try { existing = JSON.parse(raw); } catch (_e) {return;}
      if (!existing) {return;}
      const updated = {
        ...existing,
        bookingFormAnswers,
        attachments: this.attachments || existing.attachments || [],
        timestamp: Date.now(),
      };
      localStorage.setItem('book-job-chooser-restore-state', JSON.stringify(updated));
    } catch (_err) {
      // Silent — failing to persist is non-fatal.
    }
  }

  @Loading("", true)
  async loadDataAndParams() {
    try {
      const shouldShowAllToDosPage = localStorage.getItem('shouldShowAllToDosPage') === 'true';
      this.customBackPage = this.windowService.isDesktopRes ? null : (shouldShowAllToDosPage ? 'schedule' : 'schedule-list');
      this.rightSidePanelService.setDialogLoading(true);
      await this.getPageParams();
      await this.loadPageParams();
      this.showSameDayTimeAlert = LuxonDateTime.fromJSDate(this.form.controls.noEarlierDate.value).hasSame(this.currentDateTime, 'day');
      this.rightSidePanelService.setDialogLoading(false);
    } catch (_err) {
      // Silent error handling - page should continue loading
    }
  }

  async getPageParams() {
    this.hasPrivatePros = await this.pros.checkIfHasPrivatePro();
    this.hasNoPrivatePros = await this.pros.checkIfHasNoPrivatePros();
    this.serviceTypeId = await this.getParam('serviceTypeId') || 1;
    this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.customFlowName = localStorage.getItem('customFlowName');
    this.customFlowSmallImage = localStorage.getItem('customFlowSmallImage');
    this.customFlowDiscount = localStorage.getItem('customFlowDiscount');
    this.cameFromBookOrRequest = await this.getParam('cameFromBookOrRequest');
    this.hasActiveSubscription = await this.getParam('hasActiveSubscription');
    this.bookingType = await this.getParam('bookingType');
    this.planId = await this.getParam('planId');
    this.hasNonTrialPaidSubscription = await this.getParam('hasNonTrialPaidSubscription') || await this.checkIfhasNonTrialPaidSubscription();
    this.selectedFrequency = await this.getParam('selectedFrequency') || 'One Time';
    this.priceExperiment = await this.getParam('priceExperiment');
    this.bookingExperiment = await this.getParam('bookingExperiment');
    this.services = await this.getParam('services') || await this.getServices();
    this.service = await this.getParam('selectedService') || this.services[0];
    this.proId = await this.getParam('proId');
    this.address = await this.getParam('address');
    this.isPrivateFlow = await this.getParam('isPrivateFlow') ?? false;
    this.bookingId = await this.getParam('bookingId');
    this.originalJobData = await this.getParam('originalJobData');
    this.isRescheduling = await this.getParam('isRescheduling') || false;
    const today = LuxonDateTime.local().toFormat('MM-dd-yyyy');
    this.providedStartDate = await this.getParam('providedStartDate') || today;
    const parsedDate = LuxonDateTime.fromFormat(this.providedStartDate, 'MM-dd-yyyy').set({
      hour: LuxonDateTime.now().hour,
      minute: LuxonDateTime.now().minute,
      second: LuxonDateTime.now().second
    }).toJSDate();
    this.form.patchValue({
      noEarlierDate: parsedDate,
      noLaterDate: parsedDate
    });

    // Load original job notes if this is a rescheduling flow
    if (this.isRescheduling && this.originalJobData) {
      await this.loadOriginalJobNotes();
    }
  }

  async getParam(paramName): Promise<any> {
    const param = this.dialogParams?.[paramName] || this.navCtrl.getParam(paramName) || await this.storage.retrieve(paramName);
    this.storage.save(paramName, param);
    return param;
  }

  async loadPageParams() {
    this.loaded = false;
    if (this.cameFromBookOrRequest) {
      this.showMoreServices = true;
    }
    this.buildTimes();
    this.form.patchValue({
      service: this.service.key
    });
    this.currentDateTime = LuxonDateTime.fromObject({}, {
      zone: this.address.timezone,
    });
    this.allowTimesToday = await this.calendarDay.checkIfAllowTimesToday(this.currentDateTime);
    const requestDate = this.allowTimesToday ? this.currentDateTime : this.currentDateTime.plus({days: 1});
    this.dateLabel = requestDate.toFormat('M/d');
    this.selectTimesTitle = `What Times Work ${this.allowTimesToday ? 'Today' : 'Tomorrow'} ${this.dateLabel}?`;
    this.attachments = [];
    this.serviceChange();
    let prosData = await this.pros.getPros(this.serviceTypeId);
    prosData = this.pros.parseProsIntoGroups(prosData);
    this.priorityListSentence = this.pros.updatePriorityListSentence(prosData);
    this.loaded = true;
  }

  async checkIfhasNonTrialPaidSubscription() {
    const subscriptionTypes = await this.me.getSubscriptionTypes();
    this.hasNonTrialPaidSubscription = await this.me.checkIfHasNonTrialPaidSubscription(subscriptionTypes);
  }

  async getServices() {
    try {
      const _client = await this.client.getClientInfo();
      return await this.bookJobService.fetchRequiredTeamServices('all', false, this.serviceTypeId);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  handleServiceClick(service) {
    this.showMoreServices = false;
    if (this.service.key === service.key) {
      return;
    }
    this.service = service;
    this.form.patchValue({ service: service.value});
    this.didChooseService = true;
  }

  async submit() {
    const isProRequest = false;
    const isRecurring = this.form.value.frequency !== 'once';
    const isJobRequest = !isRecurring;
    const { showSameDayTimeAlert } = this;
    const bookingId = this.bookingId;
    const viewData = this.buildViewData();
    const address = this.address;
    const selectedServiceData = this.service;
    const bookData = this.getBookData();
    this.submitted = true;
    if (!this.form.valid || this.hasImageUploadError) {
      return;
    }
    const requestDates = {
      startDate: LuxonDateTime.fromJSDate(new Date(this.form.value.noEarlierDate)).toISO(),
      endDate: LuxonDateTime.fromJSDate(new Date(this.form.value.noLaterDate)).toISO()
    }
    try {
      const route = await this.getTargetPage();
      const params: any = {
        selectedServiceData,
        address,
        bookData,
        viewData,
        isJobRequest,
        showSameDayTimeAlert,
        bookingId,
        requestDates,
        customBackRoute: 'request-job',
        isProRequest,
        hasNoPrivatePros: this.hasNoPrivatePros,
        hasPrivatePros: this.hasPrivatePros,
        jobNote: this.form.value.jobNote
      };
      if (isRecurring) {
        params.bookingType = 'add_job';
      } else {
        params.bookingType = 'add_one_time_job';
      }
      const url = route;
      const component = route === 'confirm-booking-payment' ? ConfirmBookingPaymentPage : ConfirmBookingPage;
      this.rightSidePanelService.navigateTo(url, params, component);
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
      this.logger.error(err, 'request-job-get-confirm-page');
    }
  }

  // addBookingFormData method removed - now handled by dynamic-booking-form component
  // getFormValue method removed - now handled by dynamic-booking-form component

  getBookData() {
    if (this.form.value.frequency !== 'once') {
      return {
        address_id: this.address.id,
        frequency: this.form.value.frequency,
        service_type_key: this.service.key,
        dates: [{
          start_date_no_earlier_than: this.formatDate(this.form.value.noEarlierDate, 'YYYY-MM-DD'),
          start_time_no_earlier_than: this.form.value.noEarlierTime,
          end_date_no_later_than: this.formatDate(this.form.value.noLaterDate, 'YYYY-MM-DD'),
          end_time_no_later_than: this.form.value.noLaterTime,
          homekeeper_ids: []
        }],
        hk_rating: 'same_day',
        rating_chosen: false,
        backup_times_blocks: [],
        booking_form: {
          input_answers: this.buildBookingFormAnswers()
        },
        attachments: this.attachments,
        selectedCheckboxes: this.selectedCheckboxes
      };
    }
    return {
      address_id: this.address.id,
      booking: {
        service_type_key: this.service.key,
        start_date_no_earlier_than: this.formatDate(this.form.value.noEarlierDate, 'YYYY-MM-DD'),
        start_time_no_earlier_than: this.form.value.noEarlierTime,
        end_date_no_later_than: this.formatDate(this.form.value.noLaterDate, 'YYYY-MM-DD'),
        end_time_no_later_than: this.form.value.noLaterTime,
        request_type: 'waterfall'
      },
      rating_options: {hk_rating: 'same_day', rating_chosen: false},
      booking_form: {
        input_answers: this.buildBookingFormAnswers()
      },
      attachments: this.attachments,
      selectedCheckboxes: this.selectedCheckboxes
    };
  }

  /**
   * Build booking form data using the dynamic form component
   */
  private buildBookingFormAnswers() {
    if (this.dynamicBookingForm && this.dynamicBookingForm.isReady) {
      const formData = this.dynamicBookingForm.buildBookingFormData();
      return formData?.input_answers || [];
    }
    return [];
  }

  formatDate(dateTime, _format) {
    return LuxonDateTime.fromJSDate(dateTime).toFormat('yyyy-MM-dd');
  }

  async getTargetPage(): Promise<string> {
    const hasValidCC = await this.card.hasValidCard();
    const shouldRequestCC = !(this.isPrivateFlow || hasValidCC);
    return shouldRequestCC ? 'confirm-booking-payment' : 'confirm-booking';
  }

  buildViewData() {
    const frequencyLabel = this.selectedFrequency
      || this.frequencyItems.find(f => f.value === this.form.value.frequency)?.viewValue
      || 'One Time';
    return {
      service: this.service.title,
      frequency: frequencyLabel,
      price: this.service.price,
      basePrice: this.service.price,
      duration: this.service.duration,
      image: this.service.image
    };
  }

  buildTimes() {
    let numberOfOptions = 10;
    let noLaterStart = 9;
    if (this.isRentalClient || this.hasPrivatePros) {
      this.allTimesOpts = this.timeRanges.timeRangesOpts();
      this.noEarlierTimes = this.timeRanges.timeRangesOpts();
      this.noLaterTimes = this.timeRanges.timeRangesOpts();
    } else {
      if (this.service.duration === 150) {
        numberOfOptions = 9;
        noLaterStart = 11;
      } else if (this.service.duration === 240) {
        numberOfOptions = 8;
        noLaterStart = 13;
      }
      this.allTimesOpts = this.buildTimesArray(8, 11);
      this.noEarlierTimes = this.buildTimesArray(8, numberOfOptions);
      this.noLaterTimes = this.timeRanges.timeRangesOpts().filter(timeOpt => !(timeOpt.value < noLaterStart?.toString()
                || timeOpt.value > '23:00'));
    }
    this.form.patchValue({
      noEarlierTime: '08:00',
      noLaterTime: '18:30'
    });
  }

  buildTimesArray(startHour, numberOfOptions) {
    const minutesBetweenEachOption = 90;
    return Array.from({ length: numberOfOptions }, (_, index) => {
      const luxonDateTimeObject = LuxonDateTime.local().set({ hour: startHour, minute: 0, second: 0 }).plus({ minutes: index * minutesBetweenEachOption });
      const hour12Format = luxonDateTimeObject.toFormat('h:mma').toLowerCase();
      const militaryHourFormat = luxonDateTimeObject.toFormat('HH:mm');
      return { viewValue: hour12Format, value: militaryHourFormat };
    });
  }

  @Loading("", true)
  async serviceChange() {
    try {
      // Preserve the current form data before changing service
      this.preserveFormData();

      // On the FIRST serviceChange call only, pull in the bookingFormAnswers
      // passed from BookJob. preservedFormData must be set BEFORE this.forms
      // so the dynamic-booking-form component picks it up on its initial
      // componentInitialized emit (formsLoaded only fires on later changes).
      // On later calls (user picks a different service in this page),
      // preserveFormData above has already captured the latest user input —
      // don't clobber it with the original BookJob data.
      if (!this.hasLoadedIncomingFormData) {
        const incomingBookingFormAnswers = await this.getParam('bookingFormAnswers');
        if (incomingBookingFormAnswers && incomingBookingFormAnswers.input_answers?.length > 0) {
          this.preservedFormData = incomingBookingFormAnswers;
          this.attachments = (await this.getParam('attachments')) || [];
        }
        this.hasLoadedIncomingFormData = true;
      }

      this.forms = await this.bookJobService.getBookingFormTemplate(this.form.value.service);
      if (this.forms.length && !this.service.key.includes('regular_cleaning')) {
        // Dynamic form controls are now handled by the dynamic-booking-form component
        this.selectedCheckboxes = [];

        // Set loading state for dynamic form
        this.isLoadingDynamicForm = true;

        // Keep dynamic form loading until component is ready
        setTimeout(() => {
          this.isLoadingDynamicForm = false;

          // If this is a rescheduling flow and we haven't loaded the data yet, try to load it now
          if (this.isRescheduling && this.originalJobData) {
            setTimeout(() => {
              this.loadOriginalDataIfNeeded();
            }, 500); // Additional delay to ensure component is fully ready
          }
        }, 200); // Give component time to initialize
      }
      this.updateNoLaterOpts();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  // buildForm method removed - now handled by dynamic-booking-form component

  // patchBookingForm method removed - now handled by dynamic-booking-form component

  // buildSelectItems method removed - now handled by dynamic-booking-form component

  // selectCheckbox method removed - now handled by dynamic-booking-form component

  // changeSelectValue method removed - now handled by dynamic-booking-form component

  // addAttachment method removed - now handled by dynamic-booking-form component
  // getAttachment method removed - now handled by dynamic-booking-form component
  // getAttachmentName method removed - now handled by dynamic-booking-form component
  // removeAttachment method removed - now handled by dynamic-booking-form component

  selectNoEarlierTime(time) {
    if (this.form.value.noLaterDate < this.form.value.noEarlierDate
        || (this.form.value.noLaterDate <= this.form.value.noEarlierDate && time > this.form.value.noLaterTime)) {
      this.form.patchValue({noLaterTime: null});
    }
    this.showSameDayTimeAlert = LuxonDateTime.fromJSDate(this.form.controls.noEarlierDate.value).hasSame(this.currentDateTime, 'day');
    this.updateNoLaterOpts();
    this.markAllTimesSelected();
  }

  selectNoLaterTime(time) {
    this.showTimesError = false;
    if (this.isRentalClient || this.hasPrivatePros) {
      if (this.isIssueWithSelectedTimes()) {
        return this.showTimesError = true;
      } else {
        return;
      }
    }
    if (this.form.value.noLaterDate < this.form.value.noEarlierDate || (this.form.value.noLaterDate <= this.form.value.noEarlierDate && time < this.form.value.noEarlierTime)) {
      this.form.patchValue({noEarlierTime: null});
    }
    this.showSameDayTimeAlert = LuxonDateTime.fromJSDate(this.form.controls.noEarlierDate.value).hasSame(this.currentDateTime, 'day');
    this.markAllTimesSelected();
  }

  updateNoLaterOpts() {
    this.showTimesError = false;
    const noEarlierDate = this.form.value.noEarlierDate;
    const noLaterDate = this.form.value.noLaterDate;
    this.markAllTimesSelected();
    if (noEarlierDate && noLaterDate) {
      if (this.isRentalClient || this.hasPrivatePros) {
        if (this.isIssueWithSelectedTimes()) {
          return this.showTimesError = true;
        } else {
          return;
        }
      }
      if ((noEarlierDate.toString() === noLaterDate.toString())) {
        const serviceEnd = LuxonDateTime.fromFormat(this.form.value.noEarlierTime, 'HH:mm').plus({ minutes: this.service?.duration });
        this.noLaterTimes = this.timeRanges.timeRangesOpts().filter(timeOpt => {
          const optionDateTime = LuxonDateTime.fromFormat(timeOpt.value, 'HH:mm');
          return !(optionDateTime.diff(serviceEnd).as('milliseconds') < 0
                  || timeOpt.value > '23:00');
        });
      } else {
        this.noLaterTimes = this.buildTimesArray(7, 33);
      }
    } else {
      return;
    }
  }

  isIssueWithSelectedTimes() {
    const startIndex = this.timeRanges.timeRangesOpts().findIndex(item => item.value === this.form.value.noEarlierTime);
    const endIndex = this.timeRanges.timeRangesOpts().findIndex(item => item.value === this.form.value.noLaterTime);
    const numberOfItemsBetween = endIndex - startIndex - 1;
    if (startIndex < 16 || endIndex > 46) {
      return true;
    } else if (this.service.duration === 240 && (numberOfItemsBetween < 10)) {
      return true;
    } else if (this.service.duration === 150 && (numberOfItemsBetween < 7)) {
      return true;
    } else if (numberOfItemsBetween < 4) {
      return true;
    } else {
      return false;
    }
  }

  earlierDateChange(event) {
    if (!event || event === '') {
      return;
    }
    // TODO: All the current behavior should keep working, less the bug
    // TODO: Fix form is not updating it value
    this.tooFarStartDateError = false;
    this.showSameDayTimeAlert = LuxonDateTime.fromJSDate(this.form.value.noEarlierDate).hasSame(this.currentDateTime, 'day');
    if (this.showSameDayTimeAlert) {
      this.noEarlierTimes = this.allTimesOpts.filter(timeOpt => {
        const timeOptOnZone = LuxonDateTime
          .fromFormat(timeOpt.value, 'HH:mm', {zone: this.address.timezone});

        const minutesDiff = timeOptOnZone
          .diff(this.currentDateTime, 'minutes')
          .toObject().minutes;

        return minutesDiff > 10;
      });
    } else {
      this.noEarlierTimes = this.allTimesOpts;
    }
    this.noEarlierTimes = this.noEarlierTimes.filter(timeOpt => {
      const serviceEndTime = LuxonDateTime.fromFormat(timeOpt.value, 'HH:mm').plus({ minutes: this.service.duration });
      const lastTimeOpt = LuxonDateTime.fromFormat('23:00', 'HH:mm');
      return serviceEndTime.diff(lastTimeOpt, 'minutes').minutes <= 0;
    });
    this.markAllTimesSelected();
  }

  @Loading("", true)
  async markAllTimesSelected() {
    this.didSelectAllTimes = this.form.value.noEarlierDate && this.form.value.noEarlierTime && this.form.value.noLaterDate && this.form.value.noLaterTime;
    if (this.didSelectAllTimes) {
      const getBookData = this.getBookData();
      this.probabilityData = await this.bookJobService.getRequestProbability(getBookData, this.address.id);
    }
  }

  showService(service) {
    const isTheOneSelected = this.service.key === service.key;
    return !this.didChooseService
            || this.showMoreServices
            || isTheOneSelected;
  }

  showServices() {
    this.showMoreServices = true;
  }

  async turnOnTidyFindNewPros() {
    const data = {
      service_type_id: 1,
      key: 'substitutes_on'
    };
    try {
      this.waterfallSettings = await this.client.updateWaterfallSettings(data);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  goToAddPro() {
    const params = {
      categoryId: 1
    }
    const url = 'add-pro';
    const component = AddProPage;
    this.rightSidePanelService.navigateTo(url, params, component);
  }

  goToLearnMoreProbability() {
    const url = 'https://help.tidy.com/acceptance-probability';
    this.util.openUrl(url);
  }

  getTimesErrorText() {
    if (this.service.duration === 240) {
      return '5.5 hours of time for a 4 hour job.';
    } else if (this.service.duration === 150) {
      return '4 hours of time for a 2.5 hour job.';
    } else {
      return '2.5 hours of time for a 1 hour job.';
    }
  }

  goToProsSettings() {
    this.navCtrl.navigateForward('pro-settings');
  }

  /**
   * Check if this is a rescheduling flow
   */
  private isRescheduling(): boolean {
    return this.bookingType === 'reschedule_plan' || this.bookingType === 'reschedule_job';
  }

  /**
   * Load original form data if this is a rescheduling flow
   */
  private async loadOriginalDataIfNeeded() {
    try {
      // Check if this is a rescheduling flow
      if (!this.isRescheduling || !this.originalJobData) {
        return;
      }

      this.hasAttemptedToLoadOriginalData = true;

      // Get the original form answers from the job
      const originalFormAnswers = await this.schedule.getJobBookingFormAnswers(
        this.originalJobData.originalBookingId || this.bookingId
      );

      if (originalFormAnswers && originalFormAnswers.length > 0) {

        // Wait for the dynamic form component to be ready
        await this.waitForDynamicFormReady();

        // Use the safer method to load external data
        if (this.dynamicBookingForm && this.dynamicBookingForm.isReady) {

          // Check if user has already made changes to the form
          const currentUserInputs = this.preservedFormData;

          if (currentUserInputs && currentUserInputs.input_answers && currentUserInputs.input_answers.length > 0) {

            // Merge original data with user's current input, prioritizing user input
            const mergedData = this.mergeFormData(originalFormAnswers, currentUserInputs.input_answers);

            await this.dynamicBookingForm.safeLoadExternalData(mergedData);
          } else {
            // No user input yet, just load the original data
            await this.dynamicBookingForm.safeLoadExternalData(originalFormAnswers);
          }
        } else {
          // Form not ready
        }
      } else {
        // No original data to load
      }
    } catch (_err) {
      // Don't throw error - form data loading failure shouldn't break the whole flow
    }
  }

  /**
   * Restore preserved form data (separate from rescheduling data loading)
   * This restores the user's latest input values, including any updates they made
   */
  private async restorePreservedFormData() {
    if (this.preservedFormData && this.dynamicBookingForm && this.dynamicBookingForm.isReady) {

      // Check if the new forms have compatible field IDs
      const newFormFieldIds = this.forms?.[0]?.inputs?.map(input => input.id.toString()) || [];

      // Only restore data for fields that exist in the new forms
      const compatibleAnswers = this.preservedFormData.input_answers.filter(answer =>
        newFormFieldIds.includes(answer.input_id.toString())
      );

      if (compatibleAnswers.length > 0) {

        // Convert the compatible form data to the format expected by safeLoadExternalData
        const formAnswers = compatibleAnswers.map(answer => ({
          input_id: answer.input_id,
          value: answer.value
        }));

        // Load the preserved data into the new forms
        await this.dynamicBookingForm.safeLoadExternalData(formAnswers);

        // Start monitoring form changes again to keep preserved data up-to-date
        this.startFormDataMonitoring();
      } else {
        // Clear preserved data if no compatible fields
        this.stopFormDataMonitoring();
        this.preservedFormData = null;
      }
    }
  }

  /**
   * Intelligently merge preserved user input with original rescheduling data
   * This ensures user updates are preserved when switching pros in rescheduling mode
   */
  private async mergePreservedAndReschedulingData() {
    try {
      // Get the original rescheduling data
      const originalFormAnswers = await this.schedule.getJobBookingFormAnswers(
        this.originalJobData.originalBookingId || this.bookingId
      );

      if (originalFormAnswers && originalFormAnswers.length > 0) {
        // Merge the original rescheduling data with the preserved user input
        // User input should take precedence over original data
        const mergedData = this.mergeFormData(originalFormAnswers, this.preservedFormData.input_answers);

        // Load the merged data into the form
        if (this.dynamicBookingForm && this.dynamicBookingForm.isReady) {
          await this.dynamicBookingForm.safeLoadExternalData(mergedData);

          // Start monitoring form changes again to keep preserved data up-to-date
          this.startFormDataMonitoring();
        } else {
          // Form not ready
        }
      } else {
        // If no original data, just restore the preserved user input
        await this.restorePreservedFormData();
        return; // Exit early since restorePreservedFormData clears the preserved data
      }

      // Don't clear preserved data after successful merge - keep it for future updates
      // Only stop monitoring if we're not starting it again
      if (!this.dynamicBookingForm?.isReady) {
        this.stopFormDataMonitoring();
        this.preservedFormData = null;
      }

    } catch (_err) {
      // Fallback: try to restore just the preserved data
      await this.restorePreservedFormData();
    }
  }

  /**
   * Merge original form data with user's current input, prioritizing user input
   * This ensures that user updates are preserved when patching data again
   */
  private mergeFormData(originalData: any[], userInputs: any[]): any[] {
    // TODO: Fix this motherfucker logic
    // Validate input data
    if (!originalData || !Array.isArray(originalData) || !userInputs || !Array.isArray(userInputs)) {
      return originalData || [];
    }

    const mergedData = [...originalData];

    // Create a map of user inputs for quick lookup, with null checks
    const userInputMap = new Map();
    userInputs.forEach(input => {
      if (input && input.input_id !== undefined && input.input_id !== null) {
        const inputId = input.input_id.toString();
        userInputMap.set(inputId, input);
      } else {
        // Invalid input, skip
      }
    });

    // Merge user inputs with original data, prioritizing user input
    mergedData.forEach((originalItem, index) => {
      // Check for the correct ID structure based on the data format
      let originalInputId: string | null = null;

      if (originalItem && originalItem.id) {
        // Direct ID (like in request-job)
        originalInputId = originalItem.id.toString();
      } else if (originalItem && originalItem.template_input && originalItem.template_input.id) {
        // Nested template_input.id (like in select-category)
        originalInputId = originalItem.template_input.id.toString();
      }

      if (originalInputId) {
        const userInput = userInputMap.get(originalInputId);

        if (userInput) {
          // User has updated this field, use their value and preserve all user input properties
          mergedData[index] = {
            ...originalItem,
            ...userInput,
            data: {
              value: userInput?.value,
            }
          };
        }
      } else {
        // No valid input ID found
      }
    });

    // Add any new user inputs that weren't in the original data
    userInputs.forEach(userInput => {
      if (userInput && userInput.input_id !== undefined && userInput.input_id !== null) {
        const userInputId = userInput.input_id.toString();
        const exists = mergedData.some(item => {
          if (!item) {return false;}

          // Check both possible ID structures
          const itemId = item.id ? item.id.toString() :
                        (item.template_input && item.template_input.id) ? item.template_input.id.toString() : null;

          return itemId === userInputId;
        });

        if (!exists) {
          mergedData.push(userInput);
        }
      }
    });

    return mergedData;
  }

  /**
   * Preserve the current form data before changing pro/service
   * This captures the user's latest input values, not just the original data
   */
  private preserveFormData() {
    if (this.dynamicBookingForm && this.dynamicBookingForm.isReady) {
      // Get the current form data from the dynamic form component (includes user updates)
      const currentFormData = this.dynamicBookingForm.buildBookingFormData();
      if (currentFormData && currentFormData.input_answers && currentFormData.input_answers.length > 0) {
        // Store the form data temporarily
        this.preservedFormData = currentFormData;

        // Start monitoring form changes to keep preserved data up-to-date
        this.startFormDataMonitoring();
      }
    }
  }

  /**
   * Update the preserved form data with current user input
   * This ensures the preserved data is always up-to-date with user changes
   */
  private updatePreservedFormData() {
    if (this.dynamicBookingForm && this.dynamicBookingForm.isReady && this.preservedFormData) {
      // Get the current form data from the dynamic form component
      const currentFormData = this.dynamicBookingForm.buildBookingFormData();
      if (currentFormData && currentFormData.input_answers && currentFormData.input_answers.length > 0) {
        // Update the preserved data with current user input
        this.preservedFormData = currentFormData;
      }
    }
  }

  /**
   * Start continuous monitoring of form changes to keep preserved data up-to-date
   */
  private startFormDataMonitoring() {
    if (this.preservedFormData && this.dynamicBookingForm) {

      // Set up a periodic check for form changes (every 2 seconds)
      this.formDataMonitoringInterval = setInterval(() => {
        this.updatePreservedFormData();
      }, 2000);

    }
  }

  /**
   * Stop continuous monitoring of form changes
   */
  private stopFormDataMonitoring() {
    if (this.formDataMonitoringInterval) {
      clearInterval(this.formDataMonitoringInterval);
      this.formDataMonitoringInterval = null;
    }
  }

  /**
   * Wait for the dynamic form component to be ready
   */
  private async waitForDynamicFormReady(): Promise<void> {
    if (this.dynamicBookingForm) {
      await this.dynamicBookingForm.waitForReady();
    }
  }

  /**
   * Load original job notes if this is a rescheduling flow
   */
  async loadOriginalJobNotes() {
    try {
      // First try to use passed notes data if available
      if (this.originalJobData?.originalJobNotes?.length > 0) {
        // Find the text note from the passed notes
        const textNote = this.originalJobData.originalJobNotes.find(
          (note) => note.type === 'text'
        );
        if (textNote) {
          this.form.patchValue({ jobNote: textNote.text });
          return;
        }
      }

      // Fallback: Check if we have original job data with booking ID to load notes
      if (this.originalJobData?.originalBookingId) {
        // Load the original job notes
        const originalNotes = await this.schedule.getBookingNotesForJob(
          this.originalJobData.originalBookingId
        );

        if (originalNotes && originalNotes.length > 0) {
          // Find the text note from the original job
          const textNote = originalNotes.find((note) => note.type === 'text');
          if (textNote) {
            this.form.patchValue({ jobNote: textNote.text });
          }
        }
      }
    } catch (_err) {
      // Don't throw error - note loading failure shouldn't break the whole flow
    }
  }

  /**
   * Save job notes to the new booking
   */
  async saveJobNotesToNewBooking(bookingId: string) {
    if (!bookingId) {
      return;
    }

    try {
      const jobNoteText = this.form.value.jobNote;

      if (jobNoteText && jobNoteText.trim() !== '') {
        // Create new job note for the new booking
        const payload = {
          booking_id: bookingId,
          type: 'text',
          text: jobNoteText.trim(),
        };

        await this.schedule.createJobNote(payload);
      }
    } catch (_err) {
      // Don't throw error - job note saving failure shouldn't break the whole flow
    }
  }

  // Event handlers for dynamic booking form component
  onAttachmentsChange(attachments: any[]) {
    this.attachments = attachments;
  }

  onSelectedCheckboxesChange(selectedCheckboxes: any[]) {
    this.selectedCheckboxes = selectedCheckboxes;
  }

  onFormDataChange(event: any) {
    switch (event.type) {
      case 'componentInitialized':
        // If component is ready and we're in rescheduling mode, try to load original data
        if (event.isReady && this.isRescheduling && this.originalJobData) {
          this.loadOriginalDataIfNeeded();
        } else if (event.isReady && this.preservedFormData) {
          // First mount with preserved form data passed from BookJob —
          // formsLoaded only fires on subsequent ngOnChanges, so we restore
          // here on the initial componentInitialized event. Defer one tick
          // so the @ViewChild('dynamicBookingForm') ref is resolved before
          // restorePreservedFormData reads it.
          setTimeout(() => this.restorePreservedFormData(), 0);
        }
        break;

      case 'formsLoaded':
        // Check if we have both preserved data and are in rescheduling mode
        if (this.preservedFormData && this.isRescheduling && this.originalJobData) {
          this.mergePreservedAndReschedulingData();
        } else if (this.isRescheduling && this.originalJobData) {
          // If we're in rescheduling mode and have original job data, load original data
          this.loadOriginalDataIfNeeded();
        } else if (this.preservedFormData) {
          this.restorePreservedFormData();
        }
        break;

      case 'externalDataLoaded':
        break;

      case 'formDataChanged':
        // User has made changes to the form, update the preserved data if it exists
        if (this.preservedFormData) {
          this.updatePreservedFormData();
        }
        break;
    }
  }
}
